/**
 * Start over with a new family key: the screens.
 *
 * Three steps, because this cannot be undone and a parent should know exactly
 * what it does before it does it:
 *
 *   1. What goes and what stays, and typing START OVER to mean it.
 *   2. Names, typed again -- each person shown by their place in the family
 *      and their balance, which is all that is left to recognise them by.
 *   3. The new family code, and what to do about the children's tablets.
 *
 * See family/startOver.ts and services/api/valley/startover.py.
 */

import { Loading } from '../components/Spinner';
import { useEffect, useState } from 'react';
import { Secret } from '../components/Secret';
import { formatFamilyCode, joinLink } from '../family/families';
import { ShareActions } from './ShareActions';
import { dollars, type Unit } from '../family/money';
import { startOver, whatIsLeft, type WhatIsLeft } from '../family/startOver';

const CONFIRM = 'START OVER';

export function StartOver({
  familyId,
  onDone,
  onBack,
}: {
  familyId: string;
  onDone: (key: CryptoKey) => void;
  onBack: () => void;
}) {
  const [step, setStep] = useState<'warn' | 'names' | 'done'>('warn');
  const [typed, setTyped] = useState('');
  const [left, setLeft] = useState<WhatIsLeft | null>(null);
  const [familyName, setFamilyName] = useState('');
  const [names, setNames] = useState<Record<string, string>>({});
  const [unitNames, setUnitNames] = useState({ one: '', many: '', icon: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ key: CryptoKey; code: string; emailed: number } | null>(null);

  useEffect(() => {
    if (step !== 'names' || left) return;
    void whatIsLeft(familyId)
      .then(setLeft)
      .catch((problem: unknown) =>
        setError(problem instanceof Error ? problem.message : 'That did not work.'),
      );
  }, [step, left, familyId]);

  if (step === 'warn') {
    return (
      <div>
        <h2>Start over with a new key</h2>
        <p>
          Only for when nobody has the family code and no phone or computer can
          let you in. It cannot be undone.
        </p>
        <h3>What stays</h3>
        <ul>
          <li>Every amount and date in the history, and every balance.</li>
          <li>Goals, their targets and what is in them; allowance; splits.</li>
          <li>Everybody in the family, and every parent&rsquo;s account.</li>
        </ul>
        <h3>What goes</h3>
        <ul>
          <li>Names. You will type the family&rsquo;s and each person&rsquo;s again.</li>
          <li>Characters, the notes on entries, goal names and quick buttons.</li>
          <li>
            Every child&rsquo;s tablet and the wall display. They are unlinked, and
            each child gets a new code to link again at birbly.app.
          </li>
          <li>
            The old family code. Other parents type the new one once on each of
            their devices.
          </li>
        </ul>
        <p className="field-label">Type {CONFIRM} to carry on</p>
        <input
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoComplete="off"
          aria-label={`Type ${CONFIRM}`}
        />
        <div className="actions">
          <button
            className="danger-button"
            type="button"
            disabled={typed.trim().toUpperCase() !== CONFIRM}
            onClick={() => setStep('names')}
          >
            Carry on
          </button>
          <button type="button" onClick={onBack}>
            Back
          </button>
        </div>
      </div>
    );
  }

  if (step === 'done' && result) {
    return (
      <div>
        <h2>Your family has a new key</h2>
        <Secret value={formatFamilyCode(result.code)} label="Your new family code" />
        <p>
          {result.emailed > 0
            ? `We emailed ${result.emailed === 1 ? 'the other parent' : `${result.emailed} other parents`} to sign in again. `
            : ''}
          The email never has the code in it. Send it to them yourself, or have
          them tap &ldquo;Ask another device to let me in&rdquo; and say yes
          here.
        </p>
        <ShareActions code={result.code} familyName={familyName} link={joinLink(result.code)} />
        <p>
          Children: every tablet and the wall display were unlinked. In Settings,
          open each child&rsquo;s code, and have them type it at{' '}
          <strong>birbly.app/me</strong>. Old devices at the old address now send
          everybody here.
        </p>
        <div className="actions">
          <button className="primary" type="button" onClick={() => onDone(result.key)}>
            Done
          </button>
        </div>
      </div>
    );
  }

  const custom = left?.unit.type === 'custom';
  const shown: Unit | undefined = !left
    ? undefined
    : left.unit.type === 'custom'
      ? { type: 'custom', one: '', many: '', icon: '', whole: left.unit.whole }
      : (left.unit as Unit);
  const ready =
    Boolean(left) &&
    familyName.trim() !== '' &&
    left!.people.every((p) => (names[p.id] ?? '').trim() !== '') &&
    (!custom || (unitNames.one.trim() !== '' && unitNames.many.trim() !== ''));

  async function go() {
    setBusy(true);
    setError('');
    try {
      setResult(
        await startOver(familyId, {
          familyName,
          names,
          ...(custom ? { unitNames } : {}),
        }),
      );
      setStep('done');
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !busy) void go();
      }}
    >
      <h2>Names, again</h2>
      {!left && !error ? <Loading label="Checking your family…" /> : null}
      {left ? (
        <>
          <label className="field-label" htmlFor="so-family">
            Your family&rsquo;s name
          </label>
          <input
            id="so-family"
            value={familyName}
            onChange={(event) => setFamilyName(event.target.value)}
            placeholder="The Smiths"
          />
          <p className="footnote">
            Each person is shown by their place in the family and what they have
            now, so you can tell who is who.
          </p>
          {left.people.map((person, index) => (
            <div key={person.id}>
              <label className="field-label" htmlFor={`so-${person.id}`}>
                Person {index + 1} &middot; has {dollars(person.held, shown)}
              </label>
              <input
                id={`so-${person.id}`}
                value={names[person.id] ?? ''}
                onChange={(event) => setNames({ ...names, [person.id]: event.target.value })}
              />
            </div>
          ))}
          {custom ? (
            <>
              <p className="field-label">What your family counts in</p>
              <div className="row">
                <input
                  aria-label="One"
                  placeholder="star"
                  value={unitNames.one}
                  onChange={(event) => setUnitNames({ ...unitNames, one: event.target.value })}
                />
                <input
                  aria-label="Many"
                  placeholder="stars"
                  value={unitNames.many}
                  onChange={(event) => setUnitNames({ ...unitNames, many: event.target.value })}
                />
                <input
                  aria-label="Symbol"
                  placeholder="★"
                  value={unitNames.icon}
                  onChange={(event) => setUnitNames({ ...unitNames, icon: event.target.value })}
                />
              </div>
            </>
          ) : null}
        </>
      ) : null}
      {error ? <p className="error">{error}</p> : null}
      <div className="actions">
        <button aria-busy={busy} className="danger-button" type="submit" disabled={!ready || busy}>
          {busy ? 'Starting over…' : 'Start over'}
        </button>
        <button type="button" disabled={busy} onClick={onBack}>
          Back
        </button>
      </div>
    </form>
  );
}
